import { DEFAULT_LANGUAGE, SupportedLanguage, useLanguage } from "@next-platform/client-common-core";
import { useCallback } from "react";
import { NavigateOptions, type To, useNavigate } from "react-router-dom";

import useLocalizedLocation from "./useLocalizedLocation";

function pathStartsWithLang(path: string, lang: string) {
  const segments = path.split("/").filter(Boolean);
  return segments[0] === lang;
}
const normalizePath = (path: string) => {
  if (path === "/" || path === "") {
    return "";
  }

  return path.startsWith("/") ? path : `/${path}`;
};

export function useLocalizedNavigation() {
  const navigate = useNavigate();
  const { isSupportedLang } = useLocalizedLocation();
  const { currentLanguage } = useLanguage();

  const localizedNavigate = useCallback(
    (to: number | To, options?: NavigateOptions, lang?: SupportedLanguage) => {
      const navigateToLanguage = lang || (currentLanguage as SupportedLanguage);

      // Case: navigate(-1)
      if (typeof to === "number") {
        return navigate(to);
      }

      // Case: "to" is an object
      if (typeof to === "object") {
        const path = to.pathname ?? "";

        // unsupported lang normal navigate
        if (!navigateToLanguage || !isSupportedLang || navigateToLanguage === DEFAULT_LANGUAGE) {
          return navigate(to, options);
        }

        // already localized
        if (pathStartsWithLang(path, navigateToLanguage)) {
          return navigate(to, options);
        }
        const normalizedPath = path === "/" ? "" : path.startsWith("/") ? path : `/${path}`;
        const localizedPath = `/${navigateToLanguage}${normalizedPath}`;
        return navigate(
          {
            ...to,
            pathname: localizedPath,
          },
          options,
        );
      }

      // Case 3: string path
      if (typeof to === "string") {
        if (!navigateToLanguage || !isSupportedLang || navigateToLanguage === DEFAULT_LANGUAGE) {
          return navigate(to, options);
        }

        if (to.startsWith(`/${navigateToLanguage}`)) {
          return navigate(to, options);
        }
        const normalizedPath = normalizePath(to);
        const localized = `/${navigateToLanguage}${normalizedPath}`;
        return navigate(localized, options);
      }
    },
    [navigate, currentLanguage, isSupportedLang],
  );

  return localizedNavigate;
}

export default useLocalizedNavigation;
